/**
 * The draft scheduler: a two-phase, admin-run, approval vote for when the
 * league drafts. First the buddies check every DATE they can make and the
 * commissioner locks the winner; then they check every TIME slot that works for
 * that date and the commissioner locks the final time. Votes advise — the
 * commissioner always makes the call.
 *
 * Pure types and tallying live here so both the API (which validates and
 * persists) and the web app (which renders) speak the same language, and the
 * approval math is unit-tested without a database.
 */
/** A fresh poll, opened on the date phase with nothing to vote on yet. */
export function emptyDraftPoll(season) {
    return {
        season,
        phase: 'dates',
        dateOptions: [],
        dateVotes: {},
        chosenDate: null,
        timeOptions: [],
        timeVotes: {},
        final: null,
    };
}
/** The options and ballots for the phase currently open to voters. */
export function activeBallot(poll) {
    if (poll.phase === 'dates')
        return { options: poll.dateOptions, votes: poll.dateVotes };
    if (poll.phase === 'times')
        return { options: poll.timeOptions, votes: poll.timeVotes };
    return null; // 'final' — voting is closed
}
/**
 * Toggle one member's approval of one option, returning a fresh votes map: the
 * option is added if they hadn't marked it and removed if they had. A member
 * whose last pick is cleared is dropped entirely so the map never carries an
 * empty ballot. Pure, so the store persists whatever it returns.
 */
export function toggleApproval(votes, email, name, optionId) {
    const current = votes[email]?.options ?? [];
    const options = current.includes(optionId)
        ? current.filter((o) => o !== optionId)
        : [...current, optionId];
    const out = { ...votes };
    if (options.length > 0)
        out[email] = { name, options };
    else
        delete out[email];
    return out;
}
/** Drop an option from every ballot, discarding members left with none. */
export function pruneOption(votes, optionId) {
    const out = {};
    for (const [email, vote] of Object.entries(votes)) {
        const options = vote.options.filter((o) => o !== optionId);
        if (options.length > 0)
            out[email] = { name: vote.name, options };
    }
    return out;
}
/**
 * Each option with who approved it, most-approved first (ties keep the admin's
 * original ordering). This is the whole point of approval voting: the option
 * the most people can make rises to the top.
 */
export function tallyOptions(options, votes) {
    const tallies = options.map((option) => {
        const voters = Object.entries(votes)
            .filter(([, v]) => v.options.includes(option.id))
            .map(([email, v]) => ({ email, name: v.name }));
        return { option, voters, count: voters.length };
    });
    // Stable sort by count desc; equal counts keep input order.
    return tallies
        .map((t, i) => ({ t, i }))
        .sort((a, b) => b.t.count - a.t.count || a.i - b.i)
        .map(({ t }) => t);
}
/** How many distinct members have cast at least one approval. */
export function voterCount(votes) {
    return Object.keys(votes).length;
}
/**
 * The roster members who haven't cast a single approval yet — the list of
 * people to go bug. Email comparison is case-insensitive because the ballot
 * keys come from auth and the roster from the allowlist.
 */
export function nonVoters(roster, votes) {
    const voted = new Set(Object.keys(votes).map((e) => e.toLowerCase()));
    return roster.filter((m) => !voted.has(m.email.toLowerCase()));
}
//# sourceMappingURL=draftPoll.js.map